import React, { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface SmoothScrollProps {
  children: React.ReactNode;
  onScrollVelocityChange?: (velocity: number) => void;
}

export const SmoothScroll: React.FC<SmoothScrollProps> = ({
  children,
  onScrollVelocityChange,
}) => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    // On touch/mobile devices, native hardware momentum scrolling is superior and prevents compositor stutter
    const isTouch =
      typeof window !== 'undefined' &&
      (window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window || window.innerWidth < 1024);

    if (isTouch) {
      return;
    }

    let lenis: Lenis | null = null;
    let tickerCallback: ((time: number) => void) | null = null;

    try {
      // Initialize Lenis with elite easing & momentum for mouse/trackpad desktop
      lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1.0,
        touchMultiplier: 1.0,
        infinite: false,
        syncTouch: false,
      });

      lenisRef.current = lenis;

      // Synchronize Lenis scroll position with GSAP ScrollTrigger
      lenis.on('scroll', (e: any) => {
        ScrollTrigger.update();
        if (onScrollVelocityChange && typeof e.velocity === 'number') {
          onScrollVelocityChange(Math.round(e.velocity * 10) / 10);
        }
      });

      // Hook Lenis into GSAP requestAnimationFrame ticker
      tickerCallback = (time: number) => {
        if (lenis) lenis.raf(time * 1000);
      };
      gsap.ticker.add(tickerCallback);
      gsap.ticker.lagSmoothing(500, 33);
    } catch (err) {
      console.warn('[SmoothScroll] Lenis initialization skipped, fallback to native scrolling:', err);
    }

    return () => {
      if (tickerCallback) gsap.ticker.remove(tickerCallback);
      if (lenis) lenis.destroy();
      lenisRef.current = null;
    };
  }, [onScrollVelocityChange]);

  return <>{children}</>;
};
